// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { observer } from 'mobx-react';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import lowerCase from 'lodash/lowerCase';
import isEmpty from 'lodash/isEmpty';

import container from '@core/di';
import { ImportInformationType, IMPORT_INFORMATION_TYPES_LABELS } from '@shared/constants/data-management';
import { Format } from '@shared/constants/file';
import DataManagementStore from '@shared/stores/data-management';
import { DataImport as DataImportModel } from '@shared/models/data-management';
import CorruptedFileIcon from '@shared/icons/CorruptedFile';
import { FileUploadProps } from '@shared/components/FileUpload';
import { NotificationType, showNotification } from '@shared/components/Notification';
import Flex from '@shared/components/Flex';
import InitialState from './components/InitialState';
import Result, { ValidationData } from './components/Result';

import { errObj2Str } from '@shared/utils/common';

export interface DataImportProps {
  className?: string;
  type: ImportInformationType;
}

@observer
class DataImport extends React.Component<DataImportProps> {
  private dataManagementStore = container.get<DataManagementStore>(DataManagementStore.diToken);
  @observable _importedDataPreview: DataImportModel | undefined = undefined;
  private file: File;

  constructor(props: DataImportProps) {
    super(props);
    makeObservable(this);
  }

  componentDidUpdate(prevProps: DataImportProps) {
    if (this.props.type !== prevProps.type) {
      this.resetDataPreview();
    }
  }
  @computed get importedDataPreview() {
    return this._importedDataPreview;
  }

  private handleValidate = async (file: File) => {
    const { type } = this.props;

    this.file = file;

    try {
      const temp = await this.dataManagementStore.validateImport(file, type);
      runInAction(() => {
        this._importedDataPreview = temp;
      });

    } 
    catch(err) {
      showNotification(errObj2Str(err), NotificationType.error);
    }
  }

  private handleUpload = async () => {
    const { type } = this.props;

    try {
      await this.dataManagementStore.importData(this.file, type);

      showNotification('File has been successfully imported.');
      this.resetDataPreview();
    } 
    catch(err) {
      // This form of message no longer works and causes a crash.
      // const message = (
      //   <Flex alignItems="center">
      //     <CorruptedFileIcon classes={{ root: classes.corruptedFileIcon }} />
      //     <span>Import failed with error: {err}. Please reupload.</span>
      //   </Flex>
      // );

      showNotification(errObj2Str(err), NotificationType.error);
    }
  }

  private resetDataPreview = () => {
    runInAction(() => {
      this._importedDataPreview = undefined;
    });
  }

  private get normalizedData(): ValidationData {
    if (!this.importedDataPreview) {
      return {
        hasError: false,
        statuses: []
      };
    }

    const { type } = this.props;
    const { records, ...otherData } = this.importedDataPreview;
    const hasError = !isEmpty(otherData);
    const title = `${records.amount} ${lowerCase(IMPORT_INFORMATION_TYPES_LABELS[type])} recognized`;

    const statuses = Object.keys(otherData).map((errorText) => {
      const { amount, fields } = otherData[errorText];

      return {
        fields,
        key: amount !== 0 ? `${amount} ${errorText}` : errorText,
      };
    });

    return {
      title,
      hasError,
      statuses: hasError ? statuses : [{ key: 'Ready for import', fields: [] }]
    };
  }

  private get fileUploadProps(): Omit<FileUploadProps, 'onUpload' | 'classes'> {
    return {
      config: {
        multiple: false,
        accept: {
          'application/csv': [Format.csv],
        },
        maxSize: 5242880,
      }
    };
  }

  private get content() {
    if (this.importedDataPreview) {
      return (
        <Result
          data={this.normalizedData}
          uploadProps={this.fileUploadProps}
          onValidate={this.handleValidate}
          onUpload={this.handleUpload}
        />
      );
    }

    return (
      <InitialState
        uploadProps={this.fileUploadProps}
        onValidate={this.handleValidate}
      />
    );
  }

  render() {
    const classes = withStyles.getClasses(this.props);

    return (
      <div className={classes.root}>
        {this.content}
      </div>
    );
  }
}

const DataImportStyled = withStyles(
  DataImport, 
  (theme, props) => ({
    "root": {
      padding: theme.spacing(4),
      height: 'calc(100% - 56px)',
    },
    "loading": {
      marginLeft: theme.spacing(2),
    },
    "corruptedFileIcon": {
      fontSize: 16,
    }
  })
);

export default DataImportStyled;